
import { defineComponent, onBeforeMount, reactive, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, integer } from '@vuelidate/validators'

// Composable
import useNotifications from '@/common/composable/useNotifications'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { CarrierT } from '@/core/types/carriers.types'
import { CouirerCarT, CourierCarPayloadT, VehicleType } from '@/core/types/couriers.types'
import { FTLCarTypeT } from '@/core/types/ftl.types'

// Realisations
import { carriers } from '@/core/realisations/carriers.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { FTL } from '@/core/realisations/FTL.realisation'

export default defineComponent({
    components: {
        PageTitle,
        TheInput,
        TheButton,
        TheSelect,
        Loader,
    },
    setup() {
        const router = useRouter()

        const route = useRoute()
        const car_id = Number(route.params.car_id as string)

        const notifications = useNotifications()

        const is_loading = ref(false)

        const car_state = reactive({
            is_loading: false,
            is_failed: false,
            car: null as CouirerCarT | null,
        })

        const state = reactive({
            number: null as string | null,
            company_id: null as number | null,
            vehicle_type_id: null as number | null,
            model: null as string | null,
            comment: null as string | null,
            ftl_car_type_id: null as number | null,
            tech_passport_number: null as string | null,
            cubature: null as number | null,
            code_1C: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                number: { required },
                company_id: { required },
                vehicle_type_id: { required },
                model: { required },
                ftl_car_type_id: { required },
                cubature: { required, integer },
            },
            state
        )

        const submitForm = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                is_loading.value = true

                const payload: CourierCarPayloadT = {
                    number: state.number as string,
                    company_id: state.company_id as number,
                    vehicle_type_id: state.vehicle_type_id as number,
                    model: state.model as string,
                    comment: state.comment,
                    ftl_car_type_id: state.ftl_car_type_id as number,
                    tech_passport_number: state.tech_passport_number as string,
                    cubature: Number(state.cubature),
                    code_1C: state.code_1C,
                }

                await couriers.updateCar(car_id, payload)

                notifications.notify({ title: 'Машина сохранена', type: 'success' })
                router.push('/couriers/cars')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении машины произошла ошибка',
                })
            } finally {
                is_loading.value = false
            }
        }

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            vehicle_types: new DataState<VehicleType>({}),
            ftl_car_types: new DataState<FTLCarTypeT>({}),
        })

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true
                const response = await carriers.getList({ page, search, getAllCarriers: 1, statusId: 2 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        const loadVehicleTypes = async (page: number) => {
            data_state.vehicle_types.pagination.current_page = page

            try {
                data_state.vehicle_types.is_loading = true
                const response = await couriers.getVehicleTypes(page)
                data_state.vehicle_types.list = response.data

                data_state.vehicle_types.pagination.total_pages = response.meta.last_page

                data_state.vehicle_types.is_failed = false
                data_state.vehicle_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.vehicle_types.failure.catchError(error)

                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_failed = true
                }
            } finally {
                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_loading = false
                }
            }
        }

        const loadMoreVehicleTypes = async (page: number) => {
            if (page > data_state.vehicle_types.pagination.total_pages) return
            data_state.vehicle_types.pagination.current_page = page

            try {
                data_state.vehicle_types.is_loading = true

                const response = await couriers.getVehicleTypes(page)
                data_state.vehicle_types.list = data_state.vehicle_types.list.concat(response.data)

                data_state.vehicle_types.pagination.total_pages = response.meta.last_page

                data_state.vehicle_types.is_failed = false
                data_state.vehicle_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.vehicle_types.failure.catchError(error)

                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_failed = true
                }
            } finally {
                if (!data_state.vehicle_types.failure.error_state.is_cancel) {
                    data_state.vehicle_types.is_loading = false
                }
            }
        }

        const loadFtlCarTypes = async () => {
            data_state.ftl_car_types.pagination.current_page = 1

            try {
                data_state.ftl_car_types.is_loading = true
                const response = await FTL.getCarTypes()

                data_state.ftl_car_types.list = response.data
                data_state.ftl_car_types.pagination.total_pages = 1

                data_state.ftl_car_types.is_failed = false
                data_state.ftl_car_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.ftl_car_types.failure.catchError(error)

                if (!data_state.ftl_car_types.failure.error_state.is_cancel) {
                    data_state.ftl_car_types.is_failed = true
                }
            } finally {
                if (!data_state.ftl_car_types.failure.error_state.is_cancel) {
                    data_state.ftl_car_types.is_loading = false
                }
            }
        }

        const setCarToState = () => {
            if (!car_state.car) return

            state.number = car_state.car.number
            state.vehicle_type_id = car_state.car.vehicle_type_id
            state.model = car_state.car.model
            state.comment = car_state.car.comment
            state.ftl_car_type_id = car_state.car.ftl_car_type_id
            state.tech_passport_number = car_state.car.tech_passport_number
            state.cubature = car_state.car.cubature
            state.code_1C = car_state.car.code_1C

            if (car_state.car.company) {
                state.company_id = car_state.car.company.id
            }
        }

        const loadCar = async () => {
            try {
                car_state.is_loading = true

                const response = await couriers.getCar(car_id)
                car_state.car = response.data
                setCarToState()

                car_state.is_failed = false
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При получении информации об автомобиле произошла ошибка',
                })
                car_state.is_failed = true
            } finally {
                car_state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCar()
            loadCarriers(1)
            loadVehicleTypes(1)
            loadFtlCarTypes()
        })

        return {
            state,
            car_state,
            state_form$,
            is_loading,
            submitForm,
            //
            loadCar,
            data_state,
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            loadVehicleTypes,
            loadMoreVehicleTypes,
        }
    },
})
